// Pure helpers for the track & field marks ledger (Tier 3b).
//
// The backend (/api/event-marks) stores each mark as free text (`mark_value`)
// plus a numeric shadow (`mark_numeric`) and a `lower_is_better` flag that it
// uses only to derive PR/SB. Those two derived-input fields are the client's
// job to compute from the event, which is why they live here as pure,
// unit-tested functions rather than inline in the editor.
//
// Event "kind" is derived from the catalog group name (see
// backend/app/data/sport_catalog_track.py POSITIONS): the group is the single
// source of truth, so a new event added to an existing group behaves correctly
// with no change here.

// Groups whose marks are times (fastest wins) vs distances/heights (farthest
// wins) vs points totals (combined events). Mirrors the track catalog groups.
const TIMED_GROUPS = new Set(['Sprints', 'Middle Distance', 'Distance', 'Hurdles', 'Relays']);
const FIELD_GROUPS = new Set(['Jumps', 'Throws']);
const POINTS_GROUPS = new Set(['Combined']);

// Field events whose marks are wind-affected (and thus wind-readings are
// meaningful): the horizontal jumps. Vertical jumps/throws are not.
const WIND_FIELD_EVENTS = new Set(['long_jump', 'triple_jump']);

// Timed events at or below this distance (metres) are wind-affected sprints
// and short hurdles (100m, 200m, 110h). 300m+ and relays are not.
const WIND_MAX_DISTANCE_M = 200;

export const TIMING_OPTIONS = [
  { value: '', label: 'Timing…' },
  { value: 'fat', label: 'Fully Automatic (FAT)' },
  { value: 'hand', label: 'Hand Timed' },
];
export const VENUE_OPTIONS = [
  { value: '', label: 'Venue…' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'indoor', label: 'Indoor' },
];
export const TIMING_LABEL = { fat: 'FAT', hand: 'Hand' };
export const VENUE_LABEL = { outdoor: 'Outdoor', indoor: 'Indoor' };

// 'timed' | 'field' | 'points'. Unknown groups fall back to 'field' (farthest
// wins) which is the safe default for anything measured rather than timed.
export function eventKind(group) {
  if (TIMED_GROUPS.has(group)) return 'timed';
  if (POINTS_GROUPS.has(group)) return 'points';
  if (FIELD_GROUPS.has(group)) return 'field';
  return 'field';
}

// Timed events rank ascending (lower is better); field/points rank descending.
export function lowerIsBetterFor(group) {
  return eventKind(group) === 'timed';
}

export function showTimingFor(group) {
  return eventKind(group) === 'timed';
}

// Leading-integer distance of a timed event key, in metres. '110h' -> 110,
// '1600m' -> 1600, 'relay' -> null (no single distance).
function distanceMeters(eventKey) {
  const m = /^(\d+)/.exec(eventKey || '');
  return m ? parseInt(m[1], 10) : null;
}

// Wind is meaningful for short timed events (<=200m) and the horizontal jumps.
export function showWindFor(eventKey, group) {
  const kind = eventKind(group);
  if (kind === 'timed') {
    const d = distanceMeters(eventKey);
    return d != null && d <= WIND_MAX_DISTANCE_M;
  }
  if (kind === 'field') return WIND_FIELD_EVENTS.has(eventKey);
  return false;
}

// Parse the free-text mark into the numeric used only for PR/SB ranking.
// Times may be entered as "mm:ss.xx" (800m+) or plain seconds ("10.52");
// field marks and points are plain numbers (commas tolerated: "5,842").
// Returns null when the value can't be parsed — the backend still keeps the
// text, the mark just won't be eligible to be flagged PR/SB.
export function parseMarkNumeric(value, group) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const kind = eventKind(group);
  if (kind === 'timed' && s.includes(':')) {
    // [hh:]mm:ss(.xx) -> total seconds
    const parts = s.split(':').map(p => p.trim());
    if (parts.some(p => p === '' || isNaN(Number(p)))) return null;
    const nums = parts.map(Number);
    let seconds = 0;
    for (const n of nums) seconds = seconds * 60 + n;
    return Number.isFinite(seconds) ? seconds : null;
  }
  const n = parseFloat(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}
